import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";

import { handleApiError } from "@/lib/utils";
import { UNIVERSITY_ID } from "@/constants/admin";
import { upsertUniversitySettings, getUniversitySettingsBySection } from "./universitySettings";
import { UNIVERSITY_SETTINGS_SECTION_TYPES } from "@/constants/admin";
import adminAxios from "@/lib/axios";

export interface IHigherQualification {
  id?: number;
  university_settings_id?: number;
  name?: string;
  value?: string;
  priority?: number;
  variant?: string;
  is_active?: boolean;
}

/**
 * Set higher qualification variant for university (upsert)
 */
export async function setHigherQualificationVariant(variant: string, isActive: boolean = true) {
  try {
    const response = await upsertUniversitySettings(
      UNIVERSITY_SETTINGS_SECTION_TYPES.HIGHER_QUALIFICATION,
      variant,
      isActive
    );
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Create a new higher qualification
 */
export async function createHigherQualification(payload: Partial<IHigherQualification>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.HIGHER_QUALIFICATION.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get higher qualifications by university_settings_id
 */
export async function getHigherQualificationsByUniversitySettingsId(universitySettingsId: number) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.HIGHER_QUALIFICATION.GET_BY_UNIVERSITY_SETTINGS_ID(universitySettingsId)
    );
    return response?.status === 200 ? (response?.data?.data || response?.data || []) : [];
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? [] : [];
  }
}

/**
 * Update higher qualification by ID
 */
export async function updateHigherQualification(id: number, payload: Partial<IHigherQualification>) {
  try {
    const response = await adminAxios.put(ADMIN_API_ENDPOINTS.HIGHER_QUALIFICATION.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Delete higher qualification by ID
 */
export async function deleteHigherQualification(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.HIGHER_QUALIFICATION.UPDATE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get higher qualification settings (variant and status)
 */
export async function getHigherQualification() {
  try {
    const response = await getUniversitySettingsBySection(UNIVERSITY_SETTINGS_SECTION_TYPES.HIGHER_QUALIFICATION);
    if (response?.data) {
      return {
        data: {
          higher_qualification_variant: response.data.variant,
          higher_qualification_status: response.data.is_active,
          university_settings_id: response.data.id,
        }
      };
    } else if (response) {
      return {
        data: {
          higher_qualification_variant: response.variant,
          higher_qualification_status: response.is_active,
          university_settings_id: response.id,
        }
      };
    }
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

